import React, { useRef, useEffect } from 'react';
import './PortfolioShowcase.css';

const PortfolioShowcase = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const updateScales = () => {
      const containers = document.querySelectorAll('.showcase-iframe-container');
      containers.forEach(container => {
        const width = container.getBoundingClientRect().width;
        if (width > 0) {
          const scale = width / 1366;
          container.style.setProperty('--iframe-scale', scale.toString());
        }
      });
    };

    updateScales();

    const resizeObserver = new ResizeObserver(() => {
      updateScales();
    });

    const containers = document.querySelectorAll('.showcase-iframe-container');
    containers.forEach(container => resizeObserver.observe(container));

    window.addEventListener('resize', updateScales);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScales);
    };
  }, []);

  const showcaseProjects = [
    {
      title: 'H2F Studios',
      description: 'Creative digital studio delivering branding, multimedia production, and modern digital experiences.',
      url: 'https://h2f-studios.pages.dev/',
      features: ['Responsive Design', 'Modern UI', 'Fast Performance', 'Portfolio CMS'],
      technologies: ['React', 'Tailwind CSS', 'Cloudflare Pages'],
      perf: '95+',
      seo: '100'
    },
    {
      title: 'H2F Events',
      description: 'Professional event management platform designed for conferences, corporate events, and entertainment.',
      url: 'https://h2f-events.pages.dev/',
      features: ['Event Booking', 'Gallery', 'Registration', 'Schedule'],
      technologies: ['React', 'Tailwind CSS', 'Firebase'],
      perf: '96+',
      seo: '100'
    },
    {
      title: 'Fokus Lanka',
      description: 'Professional business website showcasing services and company information with a clean, responsive interface.',
      url: 'https://fokus-lanka.pages.dev/',
      features: ['Clean UI', 'Service Directory', 'Fast Speed', 'Contact Form'],
      technologies: ['React', 'Tailwind CSS', 'Cloudflare Pages'],
      perf: '98+',
      seo: '100'
    },
    {
      title: 'H2F Travels and Tours',
      description: 'Travel and tour agency website offering premium travel packages and services.',
      url: 'https://h2f-travels-and-tours.pages.dev/',
      features: ['Sliding Animation', 'Tour Packages', 'Fast Speed', 'Modern UI'],
      technologies: ['React', 'Tailwind CSS', 'Cloudflare Pages'],
      perf: '98+',
      seo: '100'
    },
    {
      title: 'Kabila Shoe Palace',
      description: 'Premium footwear e-commerce & showcase platform featuring modern product catalog, responsive design, and seamless customer experience.',
      url: 'https://kabila-shoe-palace.pages.dev/',
      features: ['Product Catalog', 'Responsive UI', 'Fast Speed', 'Modern Aesthetics'],
      technologies: ['React', 'Tailwind CSS', 'Cloudflare Pages'],
      perf: '98+',
      seo: '100'
    }
  ];

  return (
    <section id="portfolio-showcase" className="sec" ref={sectionRef}>
      <div className="sec-inner">
        <div className="sec-label">05 — Startup & Showcase</div>

        {/* Startup Section */}
        <div className="startup-section reveal">
          <div className="startup-grid">
            <div className="startup-text">
              <h2 className="sec-title"><span style={{ color: '#a00000' }}>H2F</span> — Host to Fusion</h2>
              <p className="startup-desc" style={{ marginBottom: '1.5rem', fontSize: '1.1rem', color: 'var(--text-color)' }}>
                Host To Fusion (H2F Solutions) develops world-class software, AI solutions, web applications, mobile apps, cloud infrastructure, and Salesforce CRM for businesses worldwide — from Jaffna, Sri Lanka.
              </p>
              <p className="startup-desc" style={{ marginBottom: '2rem', fontSize: '1.1rem', color: 'var(--text-color)' }}>
                Co-founded by Bijosilin Marisilin and a team of 6 batchmates. Currently working with 2 clients, delivering full-stack production projects. We utilize a premium, future-proof technology stack to construct secure, scalable, and lightning-fast software solutions that drive innovation, operational efficiency, and global business growth.
              </p>
            </div>

            <div className="startup-browser">
              <div className="showcase-browser-wrapper">
                <div className="showcase-browser-chrome">
                  <div className="chrome-dots">
                    <span className="dot dot-red"></span>
                    <span className="dot dot-yellow"></span>
                    <span className="dot dot-green"></span>
                  </div>
                  <div className="chrome-url">https://h2f.pages.dev/</div>
                </div>
                <div className="showcase-iframe-container">
                  <iframe
                    src="https://h2f.pages.dev/"
                    title="H2F Startup Preview"
                    className="showcase-iframe"
                    loading="lazy"
                    sandbox="allow-scripts allow-same-origin"
                    scrolling="no"
                    style={{ pointerEvents: 'none' }}
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Client Projects Section */}
        <div className="client-projects-section reveal" style={{ marginTop: '5rem' }}>
          <div className="client-projects-header" style={{ marginBottom: '3rem' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: '0.85rem', letterSpacing: '0.15em', color: 'var(--accent-color)', textTransform: 'uppercase', marginBottom: '1rem' }}>Explore our client projects and showcase projects</div>
            <h2 className="sec-title" style={{ marginTop: '0.5rem', marginBottom: '1rem', color: '#a00000' }}>Our Work</h2>
            <p className="client-desc" style={{ fontSize: '1.1rem', color: 'var(--text-color)', maxWidth: '600px' }}>
              We build state-of-the-art web products optimized for speed, search visibility, and maximum user engagement.
            </p>
          </div>

          <div className="showcase-grid">
            {showcaseProjects.map((project, index) => (
              <div className="showcase-card" key={index}>
                <div className="showcase-browser-wrapper">
                  <div className="showcase-browser-chrome">
                    <div className="chrome-dots">
                      <span className="dot dot-red"></span>
                      <span className="dot dot-yellow"></span>
                      <span className="dot dot-green"></span>
                    </div>
                    <div className="chrome-url">{project.url.replace('https://', '').replace('/', '')}</div>
                  </div>
                  <div className="showcase-iframe-container">
                    <iframe
                      src={project.url}
                      title={`${project.title} Preview`}
                      className="showcase-iframe"
                      loading="lazy"
                      sandbox="allow-scripts allow-same-origin"
                      scrolling="no"
                      style={{ pointerEvents: 'none' }}
                    ></iframe>
                  </div>
                </div>

                <div className="showcase-info">
                  <h4 className="showcase-project-title">{project.title}</h4>
                  <p className="showcase-project-desc">{project.description}</p>

                  <a href={project.url} target="_blank" rel="noopener noreferrer" className="showcase-live-btn">
                    Live Preview ↗
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PortfolioShowcase;
